import { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import Icon from './Icon';
import { isPdfPath } from './DocFileViewer';
import { RotatedImg, RotatedFrame } from './RotatedScan';
import { pageRotation } from '../utils/pageRotation';
import { downloadDocFile, fetchDocFileObjectUrl, formatCurrency, statusBadge } from '../utils/api';
import { toast } from './Toast';
import { formatDateShort, formatUploadedFull } from '../utils/dateFormat';
import { useIsMobile } from '../utils/useIsMobile';
import { compareDuplicate, duplicateVerdict } from '../utils/duplicateCompare';

// The compare screen for a draft flagged as a duplicate (issue #234, option
// B of the mockups): the draft's scan beside the existing document's scan, as
// large as the window allows, with a thin diff bar on top naming the fields
// that decide it (money, date, type, lines) as זהה / שונה. The admin decides
// here and nothing navigates until they do:
//
//   זה כפיל · מחק את הטיוטה   → onDelete  (the caller opens the reason dialog)
//   לא כפיל · שנה מספר מסמך   → onChangeNumber (the caller focuses the field)
//   השאר את הטיוטה · מחק את הקיים → onKeepDraft (the caller opens the reason
//                               dialog for the EXISTING document, then the
//                               draft's form — the draft was the right one)
//
// There is no "לא כפיל · אשר": the server refuses to approve a draft whose
// number collides (POST /:id/approve → 409), so the only honest ways out are
// the two above. Opened from the document page (view and edit mode) and from
// a /drafts row, so it takes the draft and the existing row as plain data and
// never reads the page's form.
//
// On a phone the two scans do not fit side by side, so they become two tabs
// under the same diff bar, and the bar itself scrolls sideways as one row so
// the scan keeps the height. On a desktop the box takes the whole window
// (the admin opened it to read two scans) and a drag handle between the
// panes gives either scan more width; the split is remembered per browser,
// double-click puts it back to half (same idiom as DocFileViewer's pane).

const SPLIT_KEY = 'dup-compare-split';
const SPLIT_MIN = 25;
const SPLIT_MAX = 75;
const readSplit = () => {
  try {
    const n = parseFloat(localStorage.getItem(SPLIT_KEY));
    return Number.isFinite(n) ? Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, n)) : 50;
  } catch { return 50; } // storage blocked — half is fine
};
const saveSplit = (n) => {
  try { localStorage.setItem(SPLIT_KEY, String(Math.round(n))); } catch (err) {
    console.warn('[dup-compare] could not save the split', err);
  }
};

function sideValue(field, value) {
  if (value == null || value === '') return '—';
  if (field.kind === 'money') return formatCurrency(value);
  if (field.kind === 'date') return formatDateShort(value) || '—';
  return String(value);
}

// A value that is a number, a date or money reads LTR inside the RTL cell
// (design-system Foundations); the doc type is Hebrew and stays RTL.
const Value = ({ field, value }) => (
  <span className="dupcmp-value" dir={field.kind === 'text' ? undefined : 'ltr'}>{sideValue(field, value)}</span>
);

// Every page of one side, the way the document page's own download does it
// (DocumentViewPage#downloadAll): one helper call per file, 250ms apart so the
// browser does not fold them into one blocked multi-download. The existing
// document's PDF also has the browser's own toolbar; the draft is usually an
// image with none, which is why both sides get this button (#243).
//
// The FIRST file starts inside the click itself, not from a timer: a
// Cloudinary scan downloads through a target=_blank link, and iOS Safari
// (nearly all the app's phone users) blocks a new tab opened outside the
// user's gesture. Later pages are best-effort, as on the document page.
const DOWNLOAD_STAGGER_MS = 250;
const downloadOne = (filePath) => {
  downloadDocFile(filePath).catch((err) => {
    console.error('[dup-compare] download failed', err);
    toast('ההורדה נכשלה — נסה שוב', 'error');
  });
};
const downloadSide = (files) => {
  files.forEach((f, i) => {
    if (i === 0) downloadOne(f.file_path);
    else setTimeout(() => downloadOne(f.file_path), i * DOWNLOAD_STAGGER_MS);
  });
};

// One scan with its own pager. Each side fetches its own blob (the /api/files
// route needs the JWT) and revokes it when the page or the dialog goes.
// `openable` adds הגדלה: a real <a> to the page, the only way to zoom a PDF on a
// phone (DocFileViewer.jsx), and a tap never closes the dialog behind it.
export function ScanPane({ files, label, head, openable = false }) {
  const [idx, setIdx] = useState(0);
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const file = files[Math.min(idx, Math.max(0, files.length - 1))];
  const fp = file?.file_path;

  useEffect(() => {
    if (!fp) { setUrl(''); return undefined; }
    let cancelled = false;
    let created = '';
    setFailed(false);
    // The previous page's blob is revoked by the cleanup below; drop it from
    // the <img> first so there is no broken-image flash while the next loads.
    setUrl('');
    fetchDocFileObjectUrl(fp).then(u => {
      if (cancelled) { if (u && u.startsWith('blob:')) URL.revokeObjectURL(u); return; }
      created = u && u.startsWith('blob:') ? u : '';
      setUrl(u || '');
    }).catch(err => {
      if (cancelled) return;
      console.error('[scan-pane] scan fetch failed', err);
      setFailed(true);
      setUrl('');
    });
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [fp]);

  const pageLabel = files.length > 1 ? `${label} · עמוד ${idx + 1}/${files.length}` : label;
  return (
    <div className="dupcmp-scan">
      {/* Head and pager share one row, so both scans start at the same height
          whether or not a side has more than one page. */}
      <div className="dupcmp-pane-head">
        {head}
        <span className="dupcmp-pane-tools">
          {files.length > 1 && (
            <span className="dupcmp-pager">
              <button type="button" className="dfv-btn dupcmp-pager-btn" onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0} aria-label="העמוד הקודם">
                <Icon name="chevron" size={14} style={{ transform: 'rotate(-90deg)' }} />
              </button>
              <span className="dupcmp-pager-label">עמוד {idx + 1}/{files.length}</span>
              <button type="button" className="dfv-btn dupcmp-pager-btn" onClick={() => setIdx(i => Math.min(files.length - 1, i + 1))} disabled={idx >= files.length - 1} aria-label="העמוד הבא">
                <Icon name="chevron" size={14} style={{ transform: 'rotate(90deg)' }} />
              </button>
            </span>
          )}
          {openable && url && (
            <a className="dfv-btn dupcmp-scan-open" href={url} target="_blank" rel="noopener noreferrer">הגדלה</a>
          )}
          {files.length > 0 && (
            <button
              type="button"
              className="dfv-btn dupcmp-pager-btn"
              onClick={() => downloadSide(files)}
              aria-label={`הורדת ${label}`}
              title={files.length > 1 ? `הורדת ${label} (${files.length} עמודים)` : `הורדת ${label}`}
            >
              <Icon name="download" size={14} />
            </button>
          )}
        </span>
      </div>
      {files.length === 0
        ? <div className="dupcmp-scan-body dupcmp-scan--empty">אין קובץ מקור</div>
        : (
          <div className="dupcmp-scan-body">
            {failed ? <div className="dfv-loading">לא ניתן לטעון את הקובץ</div>
              : !url ? <div className="dfv-loading">טוען…</div>
                : isPdfPath(fp)
                  ? <RotatedFrame src={`${url}#navpanes=0&view=FitH`} title={pageLabel} className="dfv-frame" rotation={pageRotation(file)} />
                  : <RotatedImg src={url} alt={pageLabel} className="dfv-img" rotation={pageRotation(file)} />}
          </div>
        )}
    </div>
  );
}

// The drag handle between the two panes. The body is RTL and the draft is
// its FIRST child (the right pane), so the draft's share is measured from
// the body's right edge.
function useSplit(bodyRef) {
  const [split, setSplit] = useState(readSplit);
  const [dragging, setDragging] = useState(false);
  const onPointerDown = useCallback((e) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setDragging(true);
  }, []);
  const onPointerMove = useCallback((e) => {
    if (!dragging || !bodyRef.current || !Number.isFinite(e.clientX)) return;
    const rect = bodyRef.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    const pct = ((rect.right - e.clientX) / rect.width) * 100;
    setSplit(Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, Math.round(pct * 10) / 10)));
  }, [dragging, bodyRef]);
  const onPointerUp = useCallback(() => {
    if (!dragging) return;
    setDragging(false);
    setSplit(s => { saveSplit(s); return s; });
  }, [dragging]);
  const reset = useCallback(() => { setSplit(50); saveSplit(50); }, []);
  return { split, dragging, onPointerDown, onPointerMove, onPointerUp, reset };
}

export default function DuplicateCompareDialog({ draft, existing, onClose, onDelete, onKeepDraft, onChangeNumber }) {
  const isMobile = useIsMobile(640);
  const [tab, setTab] = useState('draft');
  const fields = compareDuplicate(draft, existing);
  const verdict = duplicateVerdict(fields);
  const draftFiles = (draft?.files && draft.files.length > 0)
    ? draft.files
    : (draft?.file_path ? [{ file_path: draft.file_path, page_number: 1 }] : []);
  const existingFiles = existing?.files || [];

  const draftMeta = draft?.created_at ? `הועלה ${formatUploadedFull(draft.created_at) || ''}` : '';
  const existingMeta = [
    existing?.decided_at ? `אושר ${formatUploadedFull(existing.decided_at) || ''}` : null,
    existing?.decided_by ? `ע״י ${existing.decided_by}` : null,
  ].filter(Boolean).join(' · ');

  // On a phone both panes stay mounted and the inactive one is `hidden`, so
  // tab-hopping keeps each scan's page and blob instead of refetching it.
  // Modal's Tab trap already skips anything inside [hidden].
  const hideDraft = isMobile && tab !== 'draft';
  const hideExisting = isMobile && tab !== 'existing';
  const bodyRef = useRef(null);
  const { split, dragging, onPointerDown, onPointerMove, onPointerUp, reset } = useSplit(bodyRef);
  const paneStyle = (pct) => (isMobile ? undefined : { flex: `0 0 calc(${pct}% - 4px)` });

  return (
    <Modal onClose={onClose} ariaLabel="השוואת כפיל" size="calc(100vw - 32px)" className={`dupcmp${dragging ? ' is-dragging' : ''}`} closeOnBackdrop>
      <div className="dupcmp-head">
        <div className="dupcmp-title-wrap">
          <h2 className="dupcmp-title">השוואת כפיל</h2>
          <span className={`dupcmp-verdict ${verdict.strong ? 'is-strong' : 'is-weak'}`}>{verdict.label}</span>
        </div>
        <button type="button" className="icon-btn dupcmp-close" onClick={onClose} aria-label="סגור">
          <Icon name="close" size={18} />
        </button>
      </div>

      <ul className="dupcmp-diff" aria-label="השוואת שדות">
        {fields.map(f => (
          <li key={f.key} className={`dupcmp-cell ${f.same == null ? 'is-unknown' : f.same ? 'is-same' : 'is-diff'}`}>
            <span className="dupcmp-cell-label">{f.label}</span>
            {f.same
              ? <span className="dupcmp-cell-values"><Value field={f} value={f.draft} /></span>
              : (
                <span className="dupcmp-cell-values dupcmp-cell-values--two">
                  <span><span className="dupcmp-side">טיוטה</span> <Value field={f} value={f.draft} /></span>
                  <span><span className="dupcmp-side">קיים</span> <Value field={f} value={f.existing} /></span>
                </span>
              )}
            <span className="dupcmp-cell-state">{f.same == null ? 'לא ידוע' : f.same ? 'זהה' : 'שונה'}</span>
          </li>
        ))}
      </ul>

      {isMobile && (
        <div className="view-tabs dupcmp-tabs" role="tablist" aria-label="צד להצגה">
          <button type="button" role="tab" id="dupcmp-tab-draft" aria-controls="dupcmp-pane-draft" className={`view-tab${tab === 'draft' ? ' is-active' : ''}`} aria-selected={tab === 'draft'} onClick={() => setTab('draft')}>הטיוטה</button>
          <button type="button" role="tab" id="dupcmp-tab-existing" aria-controls="dupcmp-pane-existing" className={`view-tab${tab === 'existing' ? ' is-active' : ''}`} aria-selected={tab === 'existing'} onClick={() => setTab('existing')}>המסמך הקיים</button>
        </div>
      )}

      <div className="dupcmp-body" ref={bodyRef} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <section className="dupcmp-pane dupcmp-pane--draft" id="dupcmp-pane-draft" hidden={hideDraft} style={paneStyle(split)} role={isMobile ? 'tabpanel' : undefined} aria-labelledby={isMobile ? 'dupcmp-tab-draft' : undefined} aria-label={isMobile ? undefined : 'הטיוטה'}>
            <ScanPane
              files={draftFiles}
              label="הטיוטה"
              head={<>
                <strong>הטיוטה · #{draft?.id}</strong>
                <span className={`badge ${statusBadge('טיוטה')}`}>טיוטה</span>
                {draftMeta && <span className="dupcmp-pane-meta">{draftMeta}</span>}
              </>}
            />
          </section>
        {!isMobile && (
          <div
            className="dupcmp-divider"
            role="separator"
            aria-orientation="vertical"
            aria-label="רוחב הסריקות — גררי, לחיצה כפולה לחצי"
            aria-valuenow={Math.round(split)}
            aria-valuemin={SPLIT_MIN}
            aria-valuemax={SPLIT_MAX}
            title="גררי לשינוי הרוחב · לחיצה כפולה לחצי"
            onPointerDown={onPointerDown}
            onDoubleClick={reset}
          />
        )}
        <section className="dupcmp-pane dupcmp-pane--existing" id="dupcmp-pane-existing" hidden={hideExisting} style={paneStyle(100 - split)} role={isMobile ? 'tabpanel' : undefined} aria-labelledby={isMobile ? 'dupcmp-tab-existing' : undefined} aria-label={isMobile ? undefined : 'המסמך הקיים'}>
            <ScanPane
              files={existingFiles}
              label="המסמך הקיים"
              head={<>
                <strong>המסמך הקיים · #{existing?.id}</strong>
                <span className={`badge ${statusBadge(existing?.status)}`}>{existing?.status || '—'}</span>
                {existingMeta && <span className="dupcmp-pane-meta">{existingMeta}</span>}
              </>}
            />
          </section>
      </div>

      {/* Grouped by the document each button acts on, mirroring the panes
          above: the draft's actions on the right, the existing document's on
          the left (index.css .dupcmp-foot-group). */}
      <div className="dupcmp-foot">
        <div className="dupcmp-foot-group dupcmp-foot-group--draft" role="group" aria-label="פעולות על הטיוטה">
          <button type="button" className="btn btn-danger" onClick={onDelete}>
            <Icon name="trash" size={15} /> זה כפיל · מחק את הטיוטה
          </button>
          <button type="button" className="btn btn-outline" onClick={onChangeNumber}>
            <Icon name="edit" size={15} /> לא כפיל · שנה מספר מסמך
          </button>
        </div>
        <div className="dupcmp-foot-group dupcmp-foot-group--existing" role="group" aria-label="פעולות על המסמך הקיים">
          <button type="button" className="btn btn-danger-quiet" onClick={onKeepDraft}>
            <Icon name="trash" size={15} /> השאר את הטיוטה · מחק את הקיים
          </button>
        </div>
      </div>
    </Modal>
  );
}
